<template>
  <div>
    <!-- Header -->
    <AppHeader />

    <!-- Hero Section -->
    <section class="hero hero-eastern">
      <div class="container">
        <div class="hero-content fade-in-up">
          <h1>Наши преподаватели</h1>
          <p>Опытные и квалифицированные преподаватели китайского языка</p>
        </div>
      </div>
    </section>

    <!-- Teachers Section -->
    <UISection>
      <template #default>
        <div class="teachers-grid" v-if="!loading && !error">
          <UICard v-for="teacher in teachers" :key="teacher.id" class="teacher-card">
            <div class="teacher-photo">
              <UIAvatar :src="teacher.photo" :alt="teacher.name">
                <i class="fas fa-user"></i>
              </UIAvatar>
            </div>
            <div class="teacher-info">
              <h3>{{ teacher.name }}</h3>
              <div class="teacher-title" v-if="teacher.label">{{ teacher.label }}</div>
              <UIButton :to="`/teacher/${teacher.id}`" variant="outline">Подробнее</UIButton>
            </div>
          </UICard>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Загрузка преподавателей...</p>
        </div>

        <!-- Error state -->
        <div v-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="fetchTeachers" class="retry-button">Попробовать снова</button>
        </div>
      </template>
    </UISection>

    <!-- Testimonials Section -->
    <section v-if="reviews.length > 0" class="testimonial">
      <div class="container">
        <div class="testimonial-header">
          <h2 class="section-title" style="color: white;">Отзывы учеников</h2>
          <router-link to="/reviews" class="testimonial-link">Все отзывы</router-link>
        </div>
        <div class="testimonials-grid">
          <div class="testimonial-card" v-for="review in reviews" :key="review.id">
            <div class="testimonial-text">
              "{{ review.text }}"
            </div>
            <div class="testimonial-author">{{ review.student_name }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>Готовы начать обучение с нашими преподавателями?</h2>
        <p>Запишитесь на бесплатный пробный урок и познакомьтесь с преподавателем</p>
        <UIButton to="/contacts#contact-methods">Записаться на урок</UIButton>
      </div>
    </section>

    <!-- Footer -->
    <UIFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import UIAvatar from '@/components/ui/UIAvatar.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UICard from '@/components/ui/UICard.vue'
import UISection from '@/components/ui/UISection.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import axios from 'axios'

const api = axios.create({
  baseURL: '/api/v1'
})

export default {
  name: 'TeachersPage',
  components: {
    AppHeader,
    UIAvatar,
    UIButton,
    UICard,
    UISection,
    UIFooter
  },
  data() {
    return {
      teachers: [],
      loading: true,
      error: null,
      reviews: []
    }
  },
  async mounted() {
    await this.fetchTeachers();
    await this.loadReviews();
    // Smooth scrolling for navigation links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });

    // Add animation on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-up');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.teacher-card, .testimonial-card').forEach(el => {
      observer.observe(el);
    });
  },
  methods: {
    async fetchTeachers() {
      try {
        this.loading = true;
        const response = await fetch('/api/v1/public/teachers');
        const data = await response.json();
        
        if (response.ok) {
          // Приводим имя к формату: только имя (без фамилии)
          this.teachers = (data || []).map(t => {
            const firstName = t.first_name || (t.name ? String(t.name).split(' ')[0] : '');
            return {
              ...t,
              name: firstName || t.name || ''
            };
          });
        } else {
          this.error = 'Ошибка загрузки учителей';
        }
      } catch (error) {
        this.error = 'Ошибка соединения с сервером';
        console.error('Error fetching teachers:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadReviews() {
      try {
        const { data } = await api.get('/public/reviews/random', {
          params: { count: 3 }
        })
        this.reviews = data || []
      } catch (err) {
        console.error('Не удалось загрузить отзывы', err)
      }
    }
  }
}
</script>

<style scoped>
.nav-menu a.active {
  color: #e74c3c;
}

/* Убираем padding-top у секции с преподавателями */
:deep(.section) {
  padding-top: 0;
}

.teachers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  margin-top: 1rem;
  justify-items: center;
}

.teacher-card {
  background: #ffffff;
  padding: 2rem 1.75rem;
  border-radius: 14px;
  border: 1px solid #ebedf0; /* светлый бордер, как на about */
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.teacher-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  border-color: #e8eaee;
}

.teacher-photo {
  margin-bottom: 1.5rem;
}

.teacher-avatar {
  width: 140px; /* крупнее, как на about */
  height: 140px;
  border-radius: 12px; /* квадрат с мягкими углами */
  background: #f6f7f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: #adb5bd;
  font-size: 2.5rem;
  overflow: hidden;
  border: 1px solid #eceff3;
}

.teacher-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.teacher-info h3 {
  font-size: 1.25rem; /* чуть компактнее */
  font-weight: 700;
  margin: 0.25rem 0 0.25rem;
  color: #1f2d3d;
}

.teacher-title {
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}





.teacher-link {
  display: inline-block;
  margin-top: 1rem;
  padding: 0.55rem 1.1rem;
  border: 1px solid #e74c3c; /* брендовый акцент */
  color: #e74c3c;
  background: transparent;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.teacher-link:hover {
  background: #e74c3c;
  color: #ffffff;
}

.loading-state, .error-state {
  text-align: center;
  padding: 4rem 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retry-button {
  display: inline-block;
  background: #e74c3c;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  margin-top: 1rem;
  transition: background 0.3s ease;
  border: none;
  cursor: pointer;
}

.retry-button:hover {
  background: #c0392b;
}

@media (max-width: 768px) {
  .teachers-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .teacher-card {
    padding: 2rem 1.5rem;
  }
  
  .teacher-avatar {
    width: 110px;
    height: 110px;
    font-size: 2rem;
  }
  
  .teacher-info h3 {
    font-size: 1.3rem;
  }
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2rem;
}

.testimonial {
  padding: 80px 0;
  background: #111827;
  color: #f1f5f9;
}

.testimonial-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
}

.testimonial-link {
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.testimonial-link:hover {
  color: #ffffff;
}

.testimonial-card {
  background: rgba(15, 23, 42, 0.45);
  padding: 2.5rem;
  border-radius: 18px;
  border: 1px solid rgba(100, 116, 139, 0.35);
  box-shadow: 0 25px 50px -35px rgba(15, 23, 42, 0.8);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  color: #e2e8f0;
}

.testimonial-text {
  font-size: 1.05rem;
  line-height: 1.8;
  color: rgba(248,250,252,0.9);
}

.testimonial-author {
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(226,232,240,0.85);
  text-transform: uppercase;
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .teachers-grid {
    grid-template-columns: 1fr;
  }
  
  .teacher-card {
    padding: 1.5rem;
  }
  
  .teacher-avatar {
    width: 100px;
    height: 100px;
    font-size: 2.5rem;
  }
}
</style>
